/**
 * Game Mode Select Node - Player View
 *
 * Game mode selection with vote tracking. Compact mode cards with
 * icons, vote counts, and brand highlight on selection.
 */

import { AnimatePresence, motion } from "framer-motion";
import { Check, Users } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type GameMode = {
  id: string;
  name: string;
  description?: string;
};

type GameModeSelectState = {
  votes: Record<string, string>;
  voteCounts: Record<string, number>;
  gameModes: GameMode[];
  selectedModeId: string | null;
};

export function GameModeSelectView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<GameModeSelectState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as GameModeSelectState;
  const config = nodeState.config as {
    selectionMethod?: string;
    showVotes?: "hidden" | "count_only" | "full";
    labels?: { title?: string; subtitle?: string };
  };
  const title = config.labels?.title ?? "Game Mode";
  const subtitle = config.labels?.subtitle ?? "Vote for a game mode";
  const showVotes = config.showVotes ?? "full";
  const myVote = state.votes[userId || ""];
  const isFinalized = state.selectedModeId !== null;

  const handleSelect = async (modeId: string) => {
    if (isFinalized) return;
    setIsSubmitting(true);
    setError(null);
    const actionType = config.selectionMethod === "host_choice" ? "select" : "vote";
    const result = await onAction(actionType, { modeId, userId });
    if (!result.success && result.error) {
      setError(result.error);
    }
    setIsSubmitting(false);
  };

  const voteCount = Object.keys(state.votes).length;
  const totalPlayers = players.length;
  const totalVotesCast = Object.values(state.voteCounts).reduce((s, n) => s + n, 0);

  return (
    <NodeCard>
      <NodeHeader title={title} subtitle={subtitle} />

      {/* Vote count pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {voteCount}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {totalPlayers}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            voted
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Finalized banner */}
      <AnimatePresence>
        {isFinalized && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-6 text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.1] border border-brand-500/25">
              <Check className="size-4 text-brand-400" />
              <span className="text-sm font-display font-semibold text-brand-400 uppercase tracking-wider">
                {state.gameModes.find((m) => m.id === state.selectedModeId)?.name ?? "Selected"}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mode cards */}
      <div
        className={cn(
          "grid gap-3",
          state.gameModes.length <= 2
            ? "grid-cols-1 @md:grid-cols-2 max-w-2xl mx-auto"
            : state.gameModes.length === 3
              ? "grid-cols-1 @3xl:grid-cols-3 max-w-4xl mx-auto"
              : "grid-cols-1 @md:grid-cols-2 @3xl:grid-cols-3",
        )}
      >
        {state.gameModes.map((mode, index) => {
          const votes = state.voteCounts[mode.id] ?? 0;
          const isSelected = myVote === mode.id;
          const isFinalizedChoice = isFinalized && state.selectedModeId === mode.id;
          const votePercent = totalVotesCast > 0 ? (votes / totalVotesCast) * 100 : 0;
          const delay = Math.min(0.1 + index * 0.08, 0.5);

          return (
            <motion.button
              key={mode.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay }}
              whileHover={!isFinalized ? { scale: 1.01 } : undefined}
              whileTap={!isFinalized ? { scale: 0.99 } : undefined}
              onClick={() => handleSelect(mode.id)}
              disabled={isSubmitting || isFinalized}
              className={cn(
                "group relative rounded-lg overflow-hidden text-left transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
                "disabled:cursor-not-allowed",
                isFinalized && !isFinalizedChoice && "opacity-40",
                isSelected || isFinalizedChoice
                  ? "ring-2 ring-brand-500/60 bg-brand-500/[0.06]"
                  : "border border-surface-700/40 hover:border-surface-600/50 bg-surface-800/70",
              )}
            >
              <div className="relative px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "font-display font-bold text-sm leading-tight transition-colors",
                        isSelected || isFinalizedChoice ? "text-brand-300" : "text-white",
                      )}
                    >
                      {mode.name}
                    </p>
                    {mode.description && (
                      <p className="text-xs text-surface-400 mt-1 leading-relaxed">
                        {mode.description}
                      </p>
                    )}
                  </div>

                  {/* Vote badge */}
                  {showVotes !== "hidden" && (
                    <div
                      className={cn(
                        "shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-bold tabular-nums",
                        isSelected
                          ? "bg-black/40 text-brand-300 border border-brand-500/30"
                          : "bg-surface-700/50 text-surface-400 border border-surface-600/30",
                      )}
                    >
                      <Users className="size-3" />
                      {votes}
                    </div>
                  )}
                </div>

                {/* Vote bar */}
                {showVotes === "full" && (
                  <div className="mt-3 h-1 bg-surface-700/30 rounded-full overflow-hidden">
                    <motion.div
                      className={cn(
                        "h-full rounded-full",
                        isSelected || isFinalizedChoice
                          ? "bg-gradient-to-r from-brand-500 to-brand-400"
                          : "bg-surface-500/40",
                      )}
                      initial={{ width: 0 }}
                      animate={{ width: `${votePercent}%` }}
                      transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
                    />
                  </div>
                )}
              </div>

              {/* Top accent on selected */}
              {(isSelected || isFinalizedChoice) && (
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-400/50 to-transparent" />
              )}
            </motion.button>
          );
        })}
      </div>
    </NodeCard>
  );
}
